import React, { useState, useRef, useEffect, useMemo } from "react";
import { contactBloque } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";
import { IoIosSearch } from "react-icons/io";
import { CiFilter } from "react-icons/ci";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Mail,
  ListChecks,
  User,
  Calendar,
  Shield,
  FileText,
  MessageCircle,
  CheckCircle2,
  MapPin,
  CreditCard,
  Briefcase,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import { httpClient } from "../../../HttpClient";
import { showStatusToast } from "../../../components/ui/Toast";
import { dialog } from "../../../components/ui/Dialog";
import { useEtatGlobal } from "../../EtatGlobal";
import CreateTodo from "../../ToDo/CreateTodo";

const TNS_API = "https://backend-myalfa.vercel.app/api/suiviTNS";

const TNS_PERIODICITES = ["Mensuelle", "Trimestrielle", "Semestrielle", "Annuelle"];
const TNS_ORGANISMES = ["URSSAF", "CGSS Réunion"];
const TNS_CAISSES_RETRAITE = ["CARPIMKO", "RCI", "CIPAV", "CARMF", "CARCDSF", "IRCEC"];
const TNS_PAIEMENTS = ["Prélèvement", "Virement", "Télépaiement"];

// Résumé de la configuration TNS (champs renseignés uniquement) — aucune
// valeur par défaut affichée pour un champ vide.
const tnsResume = (c) =>
  [c.tnsRegime, c.tnsOrganisme, c.tnsCaisseRetraite, c.tnsPaiement].filter(Boolean).join(" · ") || "—";

// Options d'un filtre : choix connus + valeurs réellement présentes (texte
// libre ou valeur hors liste), sans doublon.
const filterOptions = (known, suivi, field) => [
  ...new Set([...known, ...(suivi || []).map((c) => c[field]).filter(Boolean)]),
];

function TnsConfigRows({ contact }) {
  const rows = [
    ["Régime", contact.tnsRegime],
    ["Caisse de cotisation", contact.tnsOrganisme],
    ["Caisse de retraite", contact.tnsCaisseRetraite],
    ["Périodicité", contact.tnsPeriodicite || TNS_PERIODICITES[1]],
    ["Mode de paiement", contact.tnsPaiement],
  ];
  return (
    <div className="space-y-2">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 text-xs">
          <span className="text-slate-500 shrink-0">{label}</span>
          <span className="font-medium text-slate-900 text-right">{value || "—"}</span>
        </div>
      ))}
      {contact.tnsCommentaire && (
        <div className="text-xs pt-1">
          <span className="text-slate-500 block mb-0.5">Commentaire TNS</span>
          <p className="text-slate-700 whitespace-pre-line m-0">{contact.tnsCommentaire}</p>
        </div>
      )}
    </div>
  );
}

const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const MOIS_LONG = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const STATUT_META = {
  ok: { dot: "bg-emerald-500", cellBg: "bg-emerald-50", cellBorder: "border-emerald-200", text: "text-emerald-700", label: "À jour" },
  todo: { dot: "bg-amber-400", cellBg: "bg-amber-50", cellBorder: "border-amber-200", text: "text-amber-700", label: "À traiter" },
  late: { dot: "bg-rose-500", cellBg: "bg-rose-50", cellBorder: "border-rose-200", text: "text-rose-700", label: "En retard" },
  future: { dot: "bg-slate-300", cellBg: "bg-white", cellBorder: "border-slate-200", text: "text-slate-400", label: "Période en cours" },
};

function tnsPeriods(periodicite, annee) {
  const mk = (key, label, start, span, long) => ({
    key,
    label,
    long,
    start,
    span,
    annee,
    end: new Date(annee, start + span, 0),
    due: new Date(annee, start + span + 1, 0),
  });
  if (periodicite === "Mensuelle") {
    return MOIS_COURT.map((m, i) => mk(`M${i + 1}`, m, i, 1, `${MOIS_LONG[i]} ${annee}`));
  }
  if (periodicite === "Semestrielle") {
    return [0, 1].map((i) => mk(`S${i + 1}`, `S${i + 1}`, i * 6, 6, `${i + 1}${i ? "nd" : "er"} semestre ${annee}`));
  }
  if (periodicite === "Annuelle") {
    return [mk("A", `Année ${annee}`, 0, 12, `année ${annee}`)];
  }
  return [0, 1, 2, 3].map((i) => mk(`T${i + 1}`, `T${i + 1}`, i * 3, 3, `${i + 1}${i ? "e" : "er"} trimestre ${annee}`));
}

function todayMidnight() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function periodState(periodRow, period, today) {
  if (periodRow?.declarationEffectuee && periodRow?.paiementEffectue) return "ok";
  if (today > period.due) return "late";
  if (today > period.end) return "todo";
  return "future";
}

function fmtDate(d) {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
}

function toInputDate(d) {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

const QUESTIONS_LABELS = [
  "Rencontre et attestation d'identité",
  "Personne politiquement exposée (PPE)",
  "Doutes sur l'intégrité et la réputation",
  "Gel des avoirs",
];

/* ── Overlay de détails du contact Annuaire — même principe que dans
   SuiviIR.js : lecture seule, ouvert en cliquant sur le nom du tableau. ── */
function ContactDetailOverlay({ contact, onClose }) {
  const isPPE = contact.ppe === true;
  const isIdentiteVerifiee = contact.identiteVerifiee === true;
  const contactInitials =
    `${(contact.prenoms || "").charAt(0)}${(contact.nom || "").charAt(0)}`.toUpperCase() || "?";

  const qReponses = [
    { label: QUESTIONS_LABELS[0], reponse: contact.question1Reponse, commentaire: contact.question1Commentaire },
    { label: QUESTIONS_LABELS[1], reponse: contact.question2Reponse, commentaire: contact.question2Commentaire },
    { label: QUESTIONS_LABELS[2], reponse: contact.question3Reponse, commentaire: contact.question3Commentaire },
    { label: QUESTIONS_LABELS[3], reponse: contact.question4Reponse, commentaire: contact.question4Commentaire },
  ];
  const hasQuestionnaire = qReponses.some((q) => q.reponse);

  return (
    <div className="overlayAC" onClick={onClose}>
      <div className="inoverlayLead" style={{ width: "90%", maxWidth: "860px" }} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          style={{ position: "absolute", top: 12, right: 12, background: "transparent", border: "none", cursor: "pointer", zIndex: 10000 }}
        >
          <span className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition flex items-center justify-center">
            <X className="w-4 h-4" />
          </span>
        </button>

        <div style={{ display: "flex", flexDirection: "column", width: "100%", padding: "0 5%", boxSizing: "border-box" }}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center shrink-0">
              <span className="text-base font-bold text-white">{contactInitials}</span>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">
                {contact.nom} {contact.prenoms}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {isPPE && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-md border border-amber-400 bg-amber-50 text-amber-700">
                    PPE
                  </span>
                )}
                {isIdentiteVerifiee && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-md border border-blue-300 bg-blue-50 text-blue-700 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Identité vérifiée
                  </span>
                )}
                <span className="px-2 py-1 text-xs font-semibold rounded-md border border-violet-300 bg-violet-50 text-violet-700">
                  TNS · {contact.tnsPeriodicite || TNS_PERIODICITES[1]}
                </span>
                {contact.autresMissions && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700">
                    IR
                  </span>
                )}
                {contact.clients?.length > 0 && (
                  <span className="text-xs text-slate-500">
                    {contact.clients.length} dossier{contact.clients.length > 1 ? "s" : ""} lié{contact.clients.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-5" style={{ gridTemplateColumns: contact.clients?.length > 0 ? "1fr 260px" : "1fr" }}>
            <div className="space-y-4 pb-2">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#9B1B5D]" />
                  TNS
                </h3>
                <TnsConfigRows contact={contact} />
              </div>

              {(contact.dateNaissance || contact.villeNaissance || contact.paysNaissance || contact.nationalite) && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#9B1B5D]" />
                    Identité
                  </h3>
                  <div className="space-y-2">
                    {contact.dateNaissance && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Date de naissance</span>
                        <span className="font-medium text-slate-900">{contact.dateNaissance}</span>
                      </div>
                    )}
                    {contact.villeNaissance && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Ville de naissance</span>
                        <span className="font-medium text-slate-900">{contact.villeNaissance}</span>
                      </div>
                    )}
                    {contact.paysNaissance && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Pays de naissance</span>
                        <span className="font-medium text-slate-900">{contact.paysNaissance}</span>
                      </div>
                    )}
                    {contact.nationalite && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Nationalité</span>
                        <span className="font-medium text-slate-900">{contact.nationalite}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(contact.rue || contact.codePostal || contact.villeAdresse || contact.paysAdresse) && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#9B1B5D]" />
                    Adresse de résidence
                  </h3>
                  <div className="space-y-2">
                    {contact.rue && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Rue</span>
                        <span className="font-medium text-slate-900">{contact.rue}</span>
                      </div>
                    )}
                    {contact.codePostal && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Code postal</span>
                        <span className="font-medium text-slate-900">{contact.codePostal}</span>
                      </div>
                    )}
                    {contact.villeAdresse && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Ville</span>
                        <span className="font-medium text-slate-900">{contact.villeAdresse}</span>
                      </div>
                    )}
                    {contact.paysAdresse && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Pays</span>
                        <span className="font-medium text-slate-900">{contact.paysAdresse}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(contact.email || contact.telephone) && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#9B1B5D]" />
                    Contact
                  </h3>
                  <div className="space-y-2">
                    {contact.email && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Email</span>
                        <span className="font-medium text-slate-900">{contact.email}</span>
                      </div>
                    )}
                    {contact.telephone && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Téléphone</span>
                        <span className="font-medium text-slate-900">{contact.telephone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {contact.pieceIdentite && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#9B1B5D]" />
                    Pièce d'identité
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Document</span>
                      <a
                        href={contact.pieceIdentite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#7A1533] font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Voir la pièce d'identité
                      </a>
                    </div>
                    {contact.dateEmission && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Date d'émission</span>
                        <span className="font-medium text-slate-900">{contact.dateEmission}</span>
                      </div>
                    )}
                    {contact.dateExpiration && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Date d'expiration</span>
                        <span className="font-medium text-slate-900">{contact.dateExpiration}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {hasQuestionnaire && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#9B1B5D]" />
                    Questionnaire associé / BE
                  </h3>
                  <div className="space-y-2.5">
                    {qReponses
                      .filter((q) => q.reponse)
                      .map((q, i) => (
                        <div key={i} className="flex gap-3 text-xs">
                          <span className="text-slate-500 shrink-0 w-44 leading-snug">{q.label}</span>
                          <div>
                            <span
                              className={`font-semibold ${
                                q.reponse === "Oui" ? "text-emerald-600" : q.reponse === "Non" ? "text-rose-600" : "text-slate-500"
                              }`}
                            >
                              {q.reponse}
                            </span>
                            {q.commentaire && <p className="text-slate-500 mt-0.5 italic">{q.commentaire}</p>}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {contact.commentaireGlobal && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#9B1B5D]" />
                    Commentaire global
                  </h3>
                  <p className="text-xs text-slate-700 leading-relaxed">{contact.commentaireGlobal}</p>
                </div>
              )}
            </div>

            {contact.clients?.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#9B1B5D]" />
                    Dossiers liés
                  </h3>
                  <div className="flex flex-col gap-2">
                    {contact.clients.map((c) => (
                      <div key={c.id} className="flex items-center gap-2.5 p-2.5 bg-[#9B1B5D]/5 rounded-lg border border-[#9B1B5D]/10">
                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center shrink-0">
                          <FileText className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-xs font-semibold text-slate-900 truncate">{c.nom?.toUpperCase() || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RadioCircle({ checked, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors shrink-0 ${
        checked ? "border-emerald-500 bg-emerald-500" : "border-slate-300 bg-white hover:border-slate-400"
      }`}
    >
      {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </button>
  );
}

function OuiNon({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <button
        type="button"
        onClick={() => onChange(value === true ? null : true)}
        className={`text-[11px] px-3 py-1 rounded-md border font-medium transition-colors ${
          value === true
            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
            : "border-slate-200 text-slate-400 hover:border-slate-300"
        }`}
      >
        Oui
      </button>
      <button
        type="button"
        onClick={() => onChange(value === false ? null : false)}
        className={`text-[11px] px-3 py-1 rounded-md border font-medium transition-colors ${
          value === false
            ? "border-rose-300 bg-rose-50 text-rose-600"
            : "border-slate-200 text-slate-400 hover:border-slate-300"
        }`}
      >
        Non
      </button>
    </div>
  );
}

/* ── Modale : demande d'accord de paiement au client ── */
function AccordMailModal({ contact, cfg, period, montant, author, onClose, onSent }) {
  const civ = `${contact.prenoms || ""} ${contact.nom || ""}`.trim();
  const amount =
    montant !== "" && montant !== null && montant !== undefined
      ? `${Number(montant).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
      : "[montant à préciser]";
  const [m, setM] = useState({
    to: contact.email || "",
    subject: `Cotisations sociales ${cfg.tnsOrganisme || ""} — ${period.long} : accord de paiement`,
    body:
      `Bonjour ${civ},\n\n` +
      `Dans le cadre du suivi de vos cotisations sociales de travailleur indépendant, nous avons préparé la déclaration auprès de la caisse ${cfg.tnsOrganisme || "—"} pour la période suivante : ${period.long}.\n\n` +
      `• Caisse de cotisation : ${cfg.tnsOrganisme || "—"}\n` +
      `• Période : ${period.long} (du ${fmtDate(new Date(period.end.getFullYear(), period.start, 1))} au ${fmtDate(period.end)})\n` +
      `• Montant des cotisations : ${amount}\n` +
      `• Mode de paiement : ${cfg.tnsPaiement || "—"}\n` +
      `• Date limite de paiement : ${fmtDate(period.due)}\n\n` +
      `Pourriez-vous nous confirmer, par retour de mail, votre accord pour procéder au paiement ? Nous vous remercions de vous assurer que le solde de votre compte est suffisant à cette date.\n\n` +
      `Nous restons à votre disposition pour toute question.\n\n` +
      `Bien cordialement,\n${author}\nCabinet ALFA Partenaires`,
  });
  const [sending, setSending] = useState(false);
  const up = (patch) => setM((x) => ({ ...x, ...patch }));
  const valid = /\S+@\S+\.\S+/.test(m.to);

  const send = async () => {
    if (!valid || sending) return;
    setSending(true);
    const toast = showStatusToast({
      status: "loading",
      titre: "Envoi...",
      message: "Envoi de la demande d'accord au client.",
    });
    try {
      const { data } = await httpClient.post(`${TNS_API}/${contact.id}/periode/accord-mail`, {
        annee: period.annee,
        periodeKey: period.key,
        to: m.to,
        subject: m.subject,
        body: m.body.replace(/\n/g, "<br/>"),
        author,
      });
      toast.update({
        status: "success",
        titre: "Envoyé",
        message: `Demande envoyée à ${m.to}.`,
      });
      onSent(data);
      onClose();
    } catch (err) {
      console.error("Erreur envoi accord TNS:", err);
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "L'envoi de la demande a échoué.",
      });
    } finally {
      setSending(false);
    }
  };

  const mailto = `mailto:${encodeURIComponent(m.to)}?subject=${encodeURIComponent(m.subject)}&body=${encodeURIComponent(m.body)}`;

  return (
    <div className="overlayAC" style={{ zIndex: 10050 }} onClick={onClose}>
      <div
        className="inoverlayLead"
        style={{ width: "90%", maxWidth: "560px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{ position: "absolute", top: 12, right: 12, background: "transparent", border: "none", cursor: "pointer", zIndex: 10000 }}
        >
          <span className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition flex items-center justify-center">
            <X className="w-4 h-4" />
          </span>
        </button>
        <div style={{ padding: "0 5% 20px" }}>
          <div className="mb-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 m-0">
              Mail · Demande d'accord de paiement
            </p>
            <h2 className="text-lg font-bold text-slate-900 mt-1">{civ}</h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">À</label>
              <input
                type="email"
                value={m.to}
                onChange={(e) => up({ to: e.target.value })}
                placeholder="adresse@client.fr"
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">Objet</label>
              <input
                type="text"
                value={m.subject}
                onChange={(e) => up({ subject: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">Message</label>
              <textarea
                rows={10}
                value={m.body}
                onChange={(e) => up({ body: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 resize-none"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <a href={mailto} className="text-[11px] text-slate-400 hover:text-slate-600 underline mr-auto">
              Ouvrir dans ma messagerie
            </a>
            <button
              onClick={onClose}
              className="h-8 px-4 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={send}
              disabled={!valid || sending}
              className="flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors disabled:opacity-50"
            >
              <Mail className="w-3 h-3" />
              {sending ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Missions TNS d'une case (indépendant + période) ── */

// Organismes proposés (saisie libre possible) : caisses de cotisation et de
// retraite déjà connues.
const MISSION_ORGANISMES = [...TNS_ORGANISMES, ...TNS_CAISSES_RETRAITE];

const MISSION_STATUT_STYLE = {
  "À faire": "bg-slate-100 text-slate-600",
  "En cours": "bg-blue-50 text-blue-700",
  "En attente du client": "bg-amber-50 text-amber-700",
  "En attente de l'organisme": "bg-orange-50 text-orange-700",
  "À valider": "bg-violet-50 text-violet-700",
  Terminé: "bg-emerald-50 text-emerald-700",
};

// Types et statuts chargés une seule fois par session (liste partagée),
// lancé dès l'affichage du tableau (voir SuiviTNS) : la fenêtre d'une case
// les trouve déjà en cache. La promesse en cours est partagée pour ne jamais
// faire deux requêtes en parallèle.
let missionMetaCache = null;
let missionMetaPromise = null;
function loadMissionMeta() {
  if (missionMetaCache) return Promise.resolve(missionMetaCache);
  if (!missionMetaPromise) {
    missionMetaPromise = httpClient
      .get(`${TNS_API}/mission-types`)
      .then(({ data }) => {
        missionMetaCache = data;
        return data;
      })
      .catch((err) => {
        missionMetaPromise = null; // nouvel essai à la prochaine ouverture
        throw err;
      });
  }
  return missionMetaPromise;
}

const userLabel = (u) => `${u.name || ""} ${u.lastName || ""}`.trim();
const missionEnRetard = (m, today) =>
  m.statut !== "Terminé" && m.echeance && new Date(m.echeance) < today;

const INPUT_CLS =
  "w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20";

function Champ({ label, children, full }) {
  return (
    <label className={full ? "col-span-2" : ""}>
      <span className="block text-[11px] text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

// Formulaire d'une mission (création ou modification).
function MissionForm({ initial, meta, users, onCancel, onSubmit, onNewType }) {
  const [m, setM] = useState(() => ({
    type: initial.type || "",
    organisme: initial.organisme || "",
    recurrence: initial.recurrence || "",
    responsableTNSId: initial.responsableTNSId ?? "",
    responsableTNS: initial.responsableTNS || "",
    responsableComptableId: initial.responsableComptableId ?? "",
    responsableComptable: initial.responsableComptable || "",
    statut: initial.statut || meta.statuts[0],
    echeance: toInputDate(initial.echeance),
    prochaineRelance: toInputDate(initial.prochaineRelance),
    montant: initial.montant ?? "",
    revenu: initial.revenu ?? "",
    commentaire: initial.commentaire || "",
  }));
  const [saving, setSaving] = useState(false);
  const up = (patch) => setM((x) => ({ ...x, ...patch }));

  const typesActifs = meta.types.filter((t) => t.actif || t.nom === m.type).map((t) => t.nom);
  // Un responsable désactivé reste affiché s'il est déjà choisi.
  const choixUsers = (currentId) =>
    users.filter((u) => !/inactif|archiv|désactiv/i.test(u.status || "") || u.id === Number(currentId));
  const setUser = (key) => (e) => {
    const u = users.find((x) => x.id === Number(e.target.value));
    up({ [`${key}Id`]: u ? u.id : "", [key]: u ? userLabel(u) : "" });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({
        ...m,
        responsableTNSId: m.responsableTNSId === "" ? null : Number(m.responsableTNSId),
        responsableComptableId: m.responsableComptableId === "" ? null : Number(m.responsableComptableId),
        echeance: m.echeance || null,
        prochaineRelance: m.prochaineRelance || null,
      });
    } catch {
      // erreur déjà signalée par un toast : le formulaire reste ouvert
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#9B1B5D]/20 bg-[#9B1B5D]/[0.03] p-3">
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
        <Champ label="Type de mission *" full>
          <select
            value={m.type}
            onChange={async (e) => {
              if (e.target.value !== "__nouveau__") return up({ type: e.target.value });
              const nom = await onNewType();
              if (nom) up({ type: nom });
            }}
            className={INPUT_CLS}
          >
            <option value="">— Choisir un type —</option>
            {typesActifs.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
            <option value="__nouveau__">+ Nouveau type…</option>
          </select>
        </Champ>
        <Champ label="Organisme">
          <input
            list="tns-mission-organismes"
            value={m.organisme}
            onChange={(e) => up({ organisme: e.target.value })}
            placeholder="URSSAF, CGSS, CARPIMKO…"
            className={INPUT_CLS}
          />
          <datalist id="tns-mission-organismes">
            {MISSION_ORGANISMES.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        </Champ>
        <Champ label="Ponctuelle / récurrente">
          <select value={m.recurrence} onChange={(e) => up({ recurrence: e.target.value })} className={INPUT_CLS}>
            <option value="">—</option>
            <option value="Ponctuelle">Ponctuelle</option>
            <option value="Récurrente">Récurrente</option>
          </select>
        </Champ>
        <Champ label="Responsable TNS">
          <select value={m.responsableTNSId} onChange={setUser("responsableTNS")} className={INPUT_CLS}>
            <option value="">—</option>
            {choixUsers(m.responsableTNSId).map((u) => (
              <option key={u.id} value={u.id}>{userLabel(u)}</option>
            ))}
          </select>
        </Champ>
        <Champ label="Responsable comptable">
          <select value={m.responsableComptableId} onChange={setUser("responsableComptable")} className={INPUT_CLS}>
            <option value="">—</option>
            {choixUsers(m.responsableComptableId).map((u) => (
              <option key={u.id} value={u.id}>{userLabel(u)}</option>
            ))}
          </select>
        </Champ>
        <Champ label="Statut">
          <select value={m.statut} onChange={(e) => up({ statut: e.target.value })} className={INPUT_CLS}>
            {meta.statuts.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Champ>
        <Champ label="Échéance">
          <input type="date" value={m.echeance} onChange={(e) => up({ echeance: e.target.value })} className={INPUT_CLS} />
        </Champ>
        <Champ label="Prochaine relance">
          <input
            type="date"
            value={m.prochaineRelance}
            onChange={(e) => up({ prochaineRelance: e.target.value })}
            className={INPUT_CLS}
          />
        </Champ>
        <Champ label="Montant (€)">
          <input
            type="number"
            step="0.01"
            value={m.montant}
            onChange={(e) => up({ montant: e.target.value })}
            className={INPUT_CLS}
          />
        </Champ>
        <Champ label="Revenu déclaré ou estimé (€)">
          <input
            type="number"
            step="0.01"
            value={m.revenu}
            onChange={(e) => up({ revenu: e.target.value })}
            className={INPUT_CLS}
          />
        </Champ>
        <Champ label="Commentaire (demande ou action attendue)" full>
          <textarea
            rows={2}
            value={m.commentaire}
            onChange={(e) => up({ commentaire: e.target.value })}
            className={`${INPUT_CLS} resize-y`}
          />
        </Champ>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button
          onClick={onCancel}
          className="h-7 px-3 rounded-md text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={!m.type || saving}
          className="h-7 px-3 rounded-md text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] disabled:opacity-40"
        >
          {saving ? "Enregistrement…" : "Enregistrer la mission"}
        </button>
      </div>
    </div>
  );
}

// Ajout ou modification d'une action du journal.
function ActionForm({ initial, onCancel, onSubmit }) {
  const [a, setA] = useState(() => ({
    date: toInputDate(initial?.date || new Date()),
    demarche: initial?.demarche || "",
    resultat: initial?.resultat || "",
    tempsPasse: initial?.tempsPasse ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const up = (patch) => setA((x) => ({ ...x, ...patch }));
  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({ ...a, tempsPasse: a.tempsPasse === "" ? null : a.tempsPasse });
    } catch {
      // erreur déjà signalée par un toast : le formulaire reste ouvert
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5">
      <div className="grid grid-cols-[1fr_1fr] gap-x-3 gap-y-2">
        <Champ label="Date">
          <input type="date" value={a.date} onChange={(e) => up({ date: e.target.value })} className={INPUT_CLS} />
        </Champ>
        <Champ label="Temps passé (minutes)">
          <input
            type="number"
            min="0"
            step="5"
            value={a.tempsPasse}
            onChange={(e) => up({ tempsPasse: e.target.value })}
            className={INPUT_CLS}
          />
        </Champ>
        <Champ label="Démarche effectuée *" full>
          <textarea
            rows={2}
            value={a.demarche}
            onChange={(e) => up({ demarche: e.target.value })}
            className={`${INPUT_CLS} resize-y`}
          />
        </Champ>
        <Champ label="Résultat" full>
          <input value={a.resultat} onChange={(e) => up({ resultat: e.target.value })} className={INPUT_CLS} />
        </Champ>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button
          onClick={onCancel}
          className="h-7 px-3 rounded-md text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={!a.demarche.trim() || saving}
          className="h-7 px-3 rounded-md text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] disabled:opacity-40"
        >
          {saving ? "Enregistrement…" : "Enregistrer l'action"}
        </button>
      </div>
    </div>
  );
}

// Bloc "Missions TNS" de la fenêtre d'une case : chaque mission et chaque
// action est enregistrée immédiatement (indépendamment du bouton
// "Enregistrer" de la période), puis reportée dans le tableau.
function MissionsTNS({ contact, period }) {
  const { users, utilisateur, patchSuiviTNSMissions } = useEtatGlobal();
  const today = todayMidnight();
  const [meta, setMeta] = useState(missionMetaCache);
  const [metaError, setMetaError] = useState(false);
  const [missions, setMissions] = useState(() =>
    (contact.tnsMissions || []).filter((m) => m.annee === period.annee && m.periodeKey === period.key)
  );
  const [ouverte, setOuverte] = useState(null); // id de la mission dépliée
  const [edition, setEdition] = useState(null); // "nouvelle" | id de la mission modifiée
  const [actionEdit, setActionEdit] = useState(null); // { missionId, actionId? }

  useEffect(() => {
    if (meta) return;
    loadMissionMeta()
      .then(setMeta)
      .catch((err) => {
        console.error("Erreur chargement types de mission TNS:", err);
        setMetaError(true);
      });
  }, [meta]);

  // Liste locale + tableau global mis à jour ensemble.
  const majMissions = (updater) => {
    setMissions(updater);
    patchSuiviTNSMissions(contact.id, updater);
  };
  const majMission = (id, fn) => majMissions((list) => list.map((x) => (x.id === id ? fn(x) : x)));
  const trierActions = (actions) => [...actions].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Optimiste : l'écran (fenêtre + tableau) est mis à jour immédiatement,
  // l'enregistrement part en arrière-plan avec un toast chargement →
  // confirmation ; en cas d'échec, `annuler` remet l'état précédent et le
  // toast passe en erreur.
  const envoyer = (messages, requete, { succes, annuler } = {}) => {
    const toast = showStatusToast({ status: "loading", titre: "Enregistrement...", message: messages.loading });
    return requete()
      .then(({ data }) => {
        succes?.(data);
        toast.update({ status: "success", titre: messages.success, message: "" });
        return data;
      })
      .catch((err) => {
        console.error("Erreur mission TNS:", err);
        annuler?.();
        toast.update({ status: "error", titre: "Erreur", message: messages.error });
        return null;
      });
  };

  // Valeurs du formulaire → forme affichée en attendant la réponse serveur.
  const versAffichage = (v) => ({
    ...v,
    montant: v.montant === "" || v.montant == null ? null : Number(v.montant),
    revenu: v.revenu === "" || v.revenu == null ? null : Number(v.revenu),
    tempsPasse: v.tempsPasse === "" || v.tempsPasse == null ? null : Number(v.tempsPasse),
  });
  const tempId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const auteurCourant = utilisateur ? userLabel(utilisateur) : null;

  const creerType = async () => {
    const nom = (await dialog.prompt("Nom du nouveau type de mission :", "", { title: "Nouveau type de mission" }))?.trim();
    if (!nom) return null;
    const notifierComptable = await dialog.confirm(
      "Faut-il informer le responsable comptable à la création d'une mission de ce type (ex. déclaration ou modulation de revenus) ?",
      { title: "Informer le comptable ?", confirmLabel: "Oui", cancelLabel: "Non", variant: "default" }
    );
    // Le type est proposé tout de suite ; retiré si le serveur le refuse.
    const provisoire = { id: tempId(), nom, notifierComptable, actif: true };
    missionMetaCache = { ...meta, types: [...meta.types, provisoire] };
    setMeta(missionMetaCache);
    envoyer(
      { loading: "Ajout du type…", success: "Type ajouté", error: "Ce type n'a pas pu être ajouté (il existe peut-être déjà)." },
      () => httpClient.post(`${TNS_API}/mission-types`, { nom, notifierComptable }),
      {
        succes: (data) => {
          missionMetaCache = { ...missionMetaCache, types: missionMetaCache.types.map((t) => (t.id === provisoire.id ? data : t)) };
          setMeta(missionMetaCache);
        },
        annuler: () => {
          missionMetaCache = { ...missionMetaCache, types: missionMetaCache.types.filter((t) => t.id !== provisoire.id) };
          setMeta(missionMetaCache);
        },
      }
    );
    return nom;
  };

  const creerMission = (valeurs) => {
    const id = tempId();
    const provisoire = {
      ...versAffichage(valeurs),
      id,
      annuaireId: contact.id,
      annee: period.annee,
      periodeKey: period.key,
      statut: valeurs.statut || meta.statuts[0],
      auteur: auteurCourant,
      createdAt: new Date().toISOString(),
      actions: [],
      _enCours: true,
    };
    majMissions((list) => [...list, provisoire]);
    setEdition(null);
    setOuverte(id);
    envoyer(
      { loading: "Ajout de la mission…", success: "Mission ajoutée", error: "La mission n'a pas pu être ajoutée." },
      () => httpClient.post(`${TNS_API}/${contact.id}/missions`, { ...valeurs, annee: period.annee, periodeKey: period.key }),
      {
        succes: (data) => {
          majMission(id, () => data);
          setOuverte((o) => (o === id ? data.id : o));
        },
        annuler: () => majMissions((list) => list.filter((x) => x.id !== id)),
      }
    );
  };

  const modifierMission = (mission, valeurs) => {
    const avant = mission;
    majMission(mission.id, (m) => ({ ...m, ...versAffichage(valeurs) }));
    setEdition(null);
    envoyer(
      { loading: "Mise à jour de la mission…", success: "Mission mise à jour", error: "La modification n'a pas pu être enregistrée." },
      () => httpClient.put(`${TNS_API}/missions/${mission.id}`, valeurs),
      {
        // Le journal local (actions ajoutées entre-temps) est conservé.
        succes: (data) => majMission(mission.id, (m) => ({ ...data, actions: m.actions })),
        annuler: () => majMission(mission.id, (m) => ({ ...avant, actions: m.actions })),
      }
    );
  };

  const supprimerMission = async (mission) => {
    const ok = await dialog.confirm(
      `Supprimer la mission « ${mission.type} » et tout son journal d'actions ?`,
      { title: "Supprimer la mission", confirmLabel: "Supprimer" }
    );
    if (!ok) return;
    const index = missions.findIndex((x) => x.id === mission.id);
    majMissions((list) => list.filter((x) => x.id !== mission.id));
    envoyer(
      { loading: "Suppression…", success: "Mission supprimée", error: "La suppression a échoué, la mission a été remise." },
      () => httpClient.delete(`${TNS_API}/missions/${mission.id}`),
      {
        annuler: () =>
          majMissions((list) =>
            list.some((x) => x.id === mission.id) ? list : [...list.slice(0, index), mission, ...list.slice(index)]
          ),
      }
    );
  };

  const enregistrerAction = (mission, valeurs, actionId) => {
    setActionEdit(null);
    if (actionId) {
      const avant = mission.actions.find((x) => x.id === actionId);
      majMission(mission.id, (m) => ({
        ...m,
        actions: trierActions(m.actions.map((x) => (x.id === actionId ? { ...x, ...versAffichage(valeurs) } : x))),
      }));
      envoyer(
        { loading: "Enregistrement de l'action…", success: "Action enregistrée", error: "La modification de l'action n'a pas pu être enregistrée." },
        () => httpClient.put(`${TNS_API}/missions/actions/${actionId}`, valeurs),
        {
          succes: (data) =>
            majMission(mission.id, (m) => ({ ...m, actions: trierActions(m.actions.map((x) => (x.id === actionId ? data : x))) })),
          annuler: () =>
            majMission(mission.id, (m) => ({ ...m, actions: trierActions(m.actions.map((x) => (x.id === actionId ? avant : x))) })),
        }
      );
      return;
    }
    const id = tempId();
    const provisoire = {
      ...versAffichage(valeurs),
      id,
      date: valeurs.date || new Date().toISOString(),
      auteur: auteurCourant,
      _enCours: true,
    };
    majMission(mission.id, (m) => ({ ...m, actions: trierActions([...(m.actions || []), provisoire]) }));
    envoyer(
      { loading: "Enregistrement de l'action…", success: "Action enregistrée", error: "L'action n'a pas pu être enregistrée." },
      () => httpClient.post(`${TNS_API}/missions/${mission.id}/actions`, valeurs),
      {
        succes: (data) =>
          majMission(mission.id, (m) => ({ ...m, actions: trierActions(m.actions.map((x) => (x.id === id ? data : x))) })),
        annuler: () => majMission(mission.id, (m) => ({ ...m, actions: m.actions.filter((x) => x.id !== id) })),
      }
    );
  };

  const supprimerAction = async (mission, action) => {
    const ok = await dialog.confirm("Supprimer cette action du journal ?", {
      title: "Supprimer l'action",
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    majMission(mission.id, (m) => ({ ...m, actions: m.actions.filter((x) => x.id !== action.id) }));
    envoyer(
      { loading: "Suppression…", success: "Action supprimée", error: "La suppression a échoué, l'action a été remise." },
      () => httpClient.delete(`${TNS_API}/missions/actions/${action.id}`),
      {
        annuler: () =>
          majMission(mission.id, (m) =>
            m.actions.some((x) => x.id === action.id) ? m : { ...m, actions: trierActions([...m.actions, action]) }
          ),
      }
    );
  };

  // Responsable comptable proposé par défaut : collaborateur du premier
  // dossier client lié ; responsable TNS : l'utilisateur connecté.
  const nouvelleMission = () => {
    const collab = (contact.clients || []).find((c) => c.id_collaborateur);
    return {
      responsableTNSId: utilisateur?.id ?? "",
      responsableTNS: utilisateur ? userLabel(utilisateur) : "",
      responsableComptableId: collab?.id_collaborateur ?? "",
      responsableComptable: collab?.collaborateur || "",
    };
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-slate-900 m-0 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
          Missions TNS
          {missions.length > 0 && (
            <span className="text-xs font-normal text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{missions.length}</span>
          )}
        </h3>
        {meta && edition !== "nouvelle" && (
          <button
            onClick={() => setEdition("nouvelle")}
            className="flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium border border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538] hover:text-white transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Ajouter une mission
          </button>
        )}
      </div>

      {!meta && !metaError && <p className="text-xs text-slate-400 m-0">Chargement…</p>}
      {metaError && <p className="text-xs text-rose-600 m-0">Les types de mission n'ont pas pu être chargés.</p>}

      {meta && edition === "nouvelle" && (
        <div className="mb-3">
          <MissionForm
            initial={nouvelleMission()}
            meta={meta}
            users={users || []}
            onCancel={() => setEdition(null)}
            onSubmit={creerMission}
            onNewType={creerType}
          />
        </div>
      )}

      {meta && missions.length === 0 && edition !== "nouvelle" && (
        <p className="text-xs text-slate-400 italic m-0">Aucune mission pour cette période.</p>
      )}

      <div className="space-y-2">
        {meta &&
          missions.map((mission) => {
            const deplie = ouverte === mission.id;
            const retard = missionEnRetard(mission, today);
            return (
              <div key={mission.id} className="rounded-lg border border-slate-200">
                <button
                  onClick={() => setOuverte(deplie ? null : mission.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 rounded-lg"
                >
                  <ChevronRight className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${deplie ? "rotate-90" : ""}`} />
                  <span className="text-xs font-semibold text-slate-800 flex-1 min-w-0 truncate">
                    {mission.type}
                    {mission.organisme && <span className="font-normal text-slate-500"> · {mission.organisme}</span>}
                  </span>
                  <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 shrink-0 ${MISSION_STATUT_STYLE[mission.statut] || "bg-slate-100 text-slate-600"}`}>
                    {mission.statut}
                  </span>
                  {mission.echeance && (
                    <span className={`text-[10px] shrink-0 ${retard ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
                      éch. {fmtDate(mission.echeance)}
                    </span>
                  )}
                </button>

                {deplie && (
                  <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                    {edition === mission.id ? (
                      <MissionForm
                        initial={mission}
                        meta={meta}
                        users={users || []}
                        onCancel={() => setEdition(null)}
                        onSubmit={(v) => modifierMission(mission, v)}
                        onNewType={creerType}
                      />
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {[
                            ["Ponctuelle / récurrente", mission.recurrence],
                            ["Responsable TNS", mission.responsableTNS],
                            ["Responsable comptable", mission.responsableComptable],
                            ["Prochaine relance", fmtDate(mission.prochaineRelance)],
                            ["Montant", mission.montant != null ? `${Number(mission.montant).toLocaleString("fr-FR")} €` : null],
                            ["Revenu déclaré / estimé", mission.revenu != null ? `${Number(mission.revenu).toLocaleString("fr-FR")} €` : null],
                          ].map(([label, value]) => (
                            <div key={label} className="flex justify-between gap-2">
                              <span className="text-slate-500">{label}</span>
                              <span className="font-medium text-slate-800 text-right">{value || "—"}</span>
                            </div>
                          ))}
                        </div>
                        {mission.commentaire && (
                          <p className="text-xs text-slate-600 whitespace-pre-line m-0 bg-slate-50 rounded-md px-2.5 py-2">
                            {mission.commentaire}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-slate-400">
                            {mission._enCours
                              ? "Enregistrement…"
                              : `Créée le ${fmtDate(mission.createdAt)}${mission.auteur ? ` par ${mission.auteur}` : ""}`}
                          </span>
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => setEdition(mission.id)}
                              disabled={mission._enCours}
                              className="flex items-center gap-1 h-6 px-2 rounded-md text-[11px] border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Pencil className="w-3 h-3" />
                              Modifier
                            </button>
                            <button
                              onClick={() => supprimerMission(mission)}
                              disabled={mission._enCours}
                              className="flex items-center gap-1 h-6 px-2 rounded-md text-[11px] border border-slate-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-3 h-3" />
                              Supprimer
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Journal d'actions */}
                    <div>
                      <span className="text-[11px] font-semibold text-slate-600 block mb-1.5">Journal des actions</span>
                      {(mission.actions || []).length === 0 && actionEdit?.missionId !== mission.id && (
                        <p className="text-[11px] text-slate-400 italic m-0 mb-1.5">Aucune action enregistrée.</p>
                      )}
                      <div className="space-y-1.5">
                        {(mission.actions || []).map((action) =>
                          actionEdit?.actionId === action.id ? (
                            <ActionForm
                              key={action.id}
                              initial={action}
                              onCancel={() => setActionEdit(null)}
                              onSubmit={(v) => enregistrerAction(mission, v, action.id)}
                            />
                          ) : (
                            <div key={action.id} className="relative pl-3 border-l-2 border-[#9B1B5D]/30">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <span className="text-[11px] text-slate-500">
                                    {fmtDate(action.date)}
                                    {action.auteur ? ` — ${action.auteur}` : ""}
                                    {action.tempsPasse ? ` · ${action.tempsPasse} min` : ""}
                                  </span>
                                  <p className="text-xs text-slate-700 m-0 whitespace-pre-line">{action.demarche}</p>
                                  {action.resultat && (
                                    <p className="text-xs text-slate-500 m-0">→ {action.resultat}</p>
                                  )}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                  <button
                                    onClick={() => setActionEdit({ missionId: mission.id, actionId: action.id })}
                                    disabled={action._enCours}
                                    title="Modifier l'action"
                                    className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => supprimerAction(mission, action)}
                                    disabled={action._enCours}
                                    title="Supprimer l'action"
                                    className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                      {actionEdit?.missionId === mission.id && !actionEdit.actionId ? (
                        <div className="mt-2">
                          <ActionForm
                            onCancel={() => setActionEdit(null)}
                            onSubmit={(v) => enregistrerAction(mission, v)}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => setActionEdit({ missionId: mission.id })}
                          disabled={mission._enCours}
                          className="mt-2 flex items-center gap-1 text-[11px] font-medium text-[#7A1533] hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-3 h-3" />
                          Ajouter une action
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

/* ── Modale : saisie d'une période TNS ── */
function TNSPeriodModal({ contact, period, onClose, onSaved, onOpenTodo }) {
  const { utilisateur } = useEtatGlobal();
  const authorName = `${utilisateur?.name || ""} ${utilisateur?.lastName || ""}`.trim() || "—";

  const existing = useMemo(
    () => contact.tnsPeriodes?.find((p) => p.annee === period.annee && p.periodeKey === period.key) || {},
    [contact, period]
  );

  const [f, setF] = useState({
    declarationEffectuee: !!existing.declarationEffectuee,
    declarationDate: toInputDate(existing.declarationDate),
    paiementEffectue: !!existing.paiementEffectue,
    paiementDate: toInputDate(existing.paiementDate),
    montant: existing.montant ?? "",
    modulationDemandee: existing.modulationDemandee ?? null,
    evolutionRevenus: existing.evolutionRevenus ?? null,
  });
  const [comments, setComments] = useState(Array.isArray(existing.comments) ? existing.comments : []);
  const [draft, setDraft] = useState("");
  const [accordInfo, setAccordInfo] = useState(
    existing.accordDemandeAt
      ? { ts: existing.accordDemandeAt, to: existing.accordDemandeA, author: existing.accordDemandeAuteur }
      : null
  );
  const [mailOpen, setMailOpen] = useState(false);

  const today = todayMidnight();
  const state = periodState(existing, period, today);
  const meta = STATUT_META[state];
  const up = (patch) => setF((x) => ({ ...x, ...patch }));

  const toggleDecl = () =>
    up({
      declarationEffectuee: !f.declarationEffectuee,
      declarationDate: !f.declarationEffectuee && !f.declarationDate ? toInputDate(new Date()) : f.declarationDate,
    });
  const togglePaie = () =>
    up({
      paiementEffectue: !f.paiementEffectue,
      paiementDate: !f.paiementEffectue && !f.paiementDate ? toInputDate(new Date()) : f.paiementDate,
    });

  // Optimiste : le tableau/l'overlay se mettent à jour et se ferment
  // immédiatement (sans attendre la réponse réseau), le toast chargement →
  // confirmation vient seulement informer du résultat par-dessus ; en cas
  // d'échec on republie l'état précédent avec un toast d'erreur.
  const handleSave = () => {
    const previousSnapshot = { ...existing, annuaireId: contact.id, annee: period.annee, periodeKey: period.key };
    const optimistic = {
      ...previousSnapshot,
      declarationEffectuee: f.declarationEffectuee,
      declarationDate: f.declarationDate || null,
      paiementEffectue: f.paiementEffectue,
      paiementDate: f.paiementDate || null,
      montant: f.montant === "" ? null : Number(f.montant),
      modulationDemandee: f.modulationDemandee,
      evolutionRevenus: f.evolutionRevenus,
      comments,
    };
    onSaved(optimistic);
    onClose();

    const toast = showStatusToast({
      status: "loading",
      titre: "Enregistrement...",
      message: "Mise à jour de la période en cours.",
    });
    httpClient
      .put(`${TNS_API}/${contact.id}/periode`, {
        annee: period.annee,
        periodeKey: period.key,
        declarationEffectuee: f.declarationEffectuee,
        declarationDate: f.declarationDate || null,
        paiementEffectue: f.paiementEffectue,
        paiementDate: f.paiementDate || null,
        montant: f.montant === "" ? null : f.montant,
        modulationDemandee: f.modulationDemandee,
        evolutionRevenus: f.evolutionRevenus,
      })
      .then(({ data }) => {
        onSaved(data);
        toast.update({
          status: "success",
          titre: "Période enregistrée",
          message: `${contact.nom} ${contact.prenoms} — ${period.label} ${period.annee}.`,
        });
      })
      .catch((err) => {
        console.error("Erreur enregistrement période TNS:", err);
        onSaved(previousSnapshot);
        toast.update({
          status: "error",
          titre: "Erreur",
          message: "L'enregistrement n'a pas pu être effectué.",
        });
      });
  };

  const addComment = async () => {
    const text = draft.trim();
    if (!text) return;
    const next = [...comments, { id: `c${Date.now()}`, text, author: authorName, ts: new Date().toISOString() }];
    setComments(next);
    setDraft("");
    const toast = showStatusToast({
      status: "loading",
      titre: "Envoi...",
      message: "Ajout du commentaire en cours.",
    });
    try {
      const { data } = await httpClient.put(`${TNS_API}/${contact.id}/periode`, {
        annee: period.annee,
        periodeKey: period.key,
        comments: next,
      });
      toast.update({ status: "success", titre: "Commentaire ajouté", message: "" });
      onSaved(data);
    } catch (err) {
      console.error("Erreur ajout commentaire TNS:", err);
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "L'ajout du commentaire a échoué.",
      });
    }
  };

  const onMailSent = (updatedPeriod) => {
    setAccordInfo({
      ts: updatedPeriod.accordDemandeAt,
      to: updatedPeriod.accordDemandeA,
      author: updatedPeriod.accordDemandeAuteur,
    });
    if (Array.isArray(updatedPeriod.comments)) setComments(updatedPeriod.comments);
    onSaved(updatedPeriod);
  };

  const fmtTs = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${fmtDate(d)} à ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  return (
    <>
      <div className="overlayAC" onClick={onClose}>
        <div className="inoverlayLead" style={{ width: "90%", maxWidth: "640px" }} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-3 px-[5%] mb-5">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wide flex-wrap">
                <span>Suivi TNS</span>
                <span>•</span>
                <span>{period.long.charAt(0).toUpperCase() + period.long.slice(1)}</span>
                <span>•</span>
                <span>
                  Statut : <span className={`font-bold ${meta.text}`}>{meta.label}</span>
                </span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mt-1">
                {contact.nom} {contact.prenoms}
              </h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => onOpenTodo(contact)}
                className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <ListChecks className="w-3.5 h-3.5" />
                ToDo
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="px-[5%] pb-2 space-y-4">
            <MissionsTNS contact={contact} period={period} />

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#9B1B5D]" />
                  Information indépendant
                </h3>
                <TnsConfigRows contact={contact} />
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#9B1B5D]" />
                  Période
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Début</span>
                    <span className="font-medium text-slate-900">
                      {fmtDate(new Date(period.end.getFullYear(), period.start, 1))}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Fin</span>
                    <span className="font-medium text-slate-900">{fmtDate(period.end)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Échéance</span>
                    <span className={`font-medium ${state === "late" ? "text-rose-600" : "text-slate-900"}`}>
                      {fmtDate(period.due)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#9B1B5D]" />
                Vérification
              </h3>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-600">Demande de modulation par le client ?</span>
                  <OuiNon value={f.modulationDemandee} onChange={(v) => up({ modulationDemandee: v })} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-600">
                    Évolution significative de ses revenus depuis la dernière déclaration ?
                  </span>
                  <OuiNon value={f.evolutionRevenus} onChange={(v) => up({ evolutionRevenus: v })} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#9B1B5D]" />
                Déclaration
              </h3>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-slate-600">Déclaration effectuée ?</span>
                <RadioCircle checked={f.declarationEffectuee} onClick={toggleDecl} />
              </div>
              {f.declarationEffectuee && (
                <div className="flex items-center justify-between gap-3 mt-2.5">
                  <span className="text-xs text-slate-600">Date de déclaration</span>
                  <input
                    type="date"
                    value={f.declarationDate}
                    onChange={(e) => up({ declarationDate: e.target.value })}
                    className="border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-700"
                  />
                </div>
              )}
            </div>

            <div className="px-1">
              <button
                onClick={() => setMailOpen(true)}
                className="flex items-center gap-1.5 text-xs font-medium text-[#7A1533] hover:underline"
              >
                <Mail className="w-3.5 h-3.5" />
                Demander l'accord de paiement au client
              </button>
              {accordInfo && (
                <p className="text-[11px] text-slate-400 mt-1">
                  Demande envoyée le {fmtTs(accordInfo.ts)} par {accordInfo.author || "—"}
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#9B1B5D]" />
                Paiement
              </h3>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-slate-600">Paiement effectué ?</span>
                <RadioCircle checked={f.paiementEffectue} onClick={togglePaie} />
              </div>
              {f.paiementEffectue && (
                <div className="space-y-2.5 mt-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-600">Date de paiement</span>
                    <input
                      type="date"
                      value={f.paiementDate}
                      onChange={(e) => up({ paiementDate: e.target.value })}
                      className="border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-700"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-600">Montant payé (€)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={f.montant}
                      onChange={(e) => up({ montant: e.target.value })}
                      placeholder="0,00"
                      className="border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-700 w-28 text-right"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#9B1B5D]" />
                Commentaires
                {comments.length > 0 && (
                  <span className="ml-auto text-xs font-normal text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">
                    {comments.length}
                  </span>
                )}
              </h3>
              {comments.length > 0 && (
                <div className="space-y-2.5 mb-3">
                  {comments.map((m) => (
                    <div key={m.id} className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#9B1B5D]/10 text-[#9B1B5D] text-[10px] font-bold flex items-center justify-center shrink-0">
                        {initials(m.author) || "?"}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="font-semibold text-slate-700">{m.author}</span>
                          {m.ts && <span className="text-slate-400">{fmtTs(m.ts)}</span>}
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">{m.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  rows={2}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  className="flex-1 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
                />
                <button
                  onClick={addComment}
                  disabled={!draft.trim()}
                  className="h-8 px-3 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors disabled:opacity-40"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-[5%] pt-4 pb-5 border-t border-slate-100 mt-4">
            <button
              onClick={onClose}
              className="h-8 px-4 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors"
            >
              <Check className="w-3 h-3" />
              Enregistrer
            </button>
          </div>
        </div>
      </div>
      {mailOpen && (
        <AccordMailModal
          contact={contact}
          cfg={contact}
          period={period}
          montant={f.montant}
          author={authorName}
          onClose={() => setMailOpen(false)}
          onSent={onMailSent}
        />
      )}
    </>
  );
}

const FILTRES_VIDES = {
  organisme: [],
  retraite: [],
  periodicite: [],
  regime: [],
  paiement: [],
  statut: [],
  // Filtres sur les missions de l'année affichée : un indépendant est gardé
  // s'il a au moins UNE mission qui remplit tous les critères choisis.
  missionResponsable: [],
  missionStatut: [],
  missionOrganisme: [],
};

const missionsDeLAnnee = (contact, annee) => (contact.tnsMissions || []).filter((m) => m.annee === annee);

/* ── Écran principal : Suivi TNS ── */
function SuiviTNS({ suivi, loading }) {
  const { fetchTodos, patchSuiviTNSPeriode } = useEtatGlobal();
  const today = todayMidnight();
  const [year, setYear] = useState(today.getFullYear());
  const [search, setSearch] = useState("");
  const [flt, setFlt] = useState({ ...FILTRES_VIDES });
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [edit, setEdit] = useState(null);
  const [todoContact, setTodoContact] = useState(null);
  const [detailContact, setDetailContact] = useState(null);

  useEffect(() => {
    if (!filterOpen) return;
    const onDoc = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [filterOpen]);

  // Types de mission préchargés avec le tableau : la fenêtre d'une case
  // s'ouvre ensuite sans temps de chargement du bloc Missions TNS.
  useEffect(() => {
    loadMissionMeta().catch((err) => console.error("Préchargement types de mission TNS:", err));
  }, []);

  const toggleFiltre = (groupe, valeur) =>
    setFlt((x) => ({
      ...x,
      [groupe]: x[groupe].includes(valeur) ? x[groupe].filter((v) => v !== valeur) : [...x[groupe], valeur],
    }));
  const filtreCount = Object.values(flt).reduce((n, a) => n + a.length, 0);

  const list = useMemo(() => {
    const rowStatuts = (contact) =>
      new Set(
        tnsPeriods(contact.tnsPeriodicite || "Trimestrielle", year).map((p) => {
          const row = contact.tnsPeriodes?.find((r) => r.annee === year && r.periodeKey === p.key);
          return periodState(row, p, today);
        })
      );

    const q = search.trim().toLowerCase();
    return (suivi || []).filter((c) => {
      const matchesSearch =
        !q ||
        `${c.nom || ""} ${c.prenoms || ""}`.toLowerCase().includes(q) ||
        (c.clients || []).some((cl) => (cl.nom || "").toLowerCase().includes(q));
      const matchesConfig =
        (!flt.organisme.length || flt.organisme.includes(c.tnsOrganisme)) &&
        (!flt.periodicite.length || flt.periodicite.includes(c.tnsPeriodicite)) &&
        (!flt.regime.length || flt.regime.includes(c.tnsRegime)) &&
        (!flt.retraite.length || flt.retraite.includes(c.tnsCaisseRetraite)) &&
        (!flt.paiement.length || flt.paiement.includes(c.tnsPaiement));
      const matchesStatut =
        !flt.statut.length ||
        (() => {
          const s = rowStatuts(c);
          return flt.statut.some((v) =>
            v === "ok" ? ![...s].some((x) => x === "late" || x === "todo") : s.has(v)
          );
        })();
      const filtreMission = flt.missionResponsable.length || flt.missionStatut.length || flt.missionOrganisme.length;
      const matchesMission =
        !filtreMission ||
        missionsDeLAnnee(c, year).some(
          (m) =>
            (!flt.missionResponsable.length ||
              flt.missionResponsable.includes(m.responsableTNS) ||
              flt.missionResponsable.includes(m.responsableComptable)) &&
            (!flt.missionStatut.length || flt.missionStatut.includes(m.statut)) &&
            (!flt.missionOrganisme.length || flt.missionOrganisme.includes(m.organisme))
        );
      return matchesSearch && matchesConfig && matchesStatut && matchesMission;
    });
  }, [suivi, search, flt, year, today]);

  // Valeurs proposées dans les filtres de missions : celles réellement
  // saisies sur l'année affichée (statuts : liste complète).
  const missionsAnnee = useMemo(() => (suivi || []).flatMap((c) => missionsDeLAnnee(c, year)), [suivi, year]);
  const valeursMission = (champs) =>
    [...new Set(missionsAnnee.flatMap((m) => champs.map((k) => m[k])).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, "fr")
    );

  const curMonth = year === today.getFullYear() ? today.getMonth() : -1;

  const handleSaved = (updatedPeriod) => {
    patchSuiviTNSPeriode(updatedPeriod.annuaireId, updatedPeriod);
    setEdit((prev) => {
      if (!prev) return prev;
      const existingList = prev.contact.tnsPeriodes || [];
      const idx = existingList.findIndex(
        (p) => p.annee === updatedPeriod.annee && p.periodeKey === updatedPeriod.periodeKey
      );
      const mergedPeriodes =
        idx === -1
          ? [...existingList, updatedPeriod]
          : existingList.map((p, i) => (i === idx ? { ...p, ...updatedPeriod } : p));
      return { ...prev, contact: { ...prev.contact, tnsPeriodes: mergedPeriodes } };
    });
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="h-9 w-72 rounded-xl bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-2">
            <div className="h-9 w-24 rounded-lg bg-slate-200 animate-pulse" />
            <div className="h-9 w-28 rounded-lg bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white" style={{ fontSize: "0.72rem" }}>
            <thead>
              <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
                {["Indépendant", "Périodicité", ...MOIS_COURT].map((col, i) => (
                  <th key={i} className="px-3 py-2.5 text-left">
                    <div className="h-3 rounded bg-[#ffffff33] animate-pulse" style={{ width: "70%" }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="bg-white">
                  <td className="px-3 py-3">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-32 mb-1" />
                    <div className="h-2 rounded bg-slate-100 animate-pulse w-20" />
                  </td>
                  <td className="px-3 py-3">
                    <div className="h-5 w-20 rounded-full bg-slate-200 animate-pulse" />
                  </td>
                  {MOIS_COURT.map((_, j) => (
                    <td key={j} className="px-3 py-3">
                      <div className="h-6 rounded bg-slate-100 animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un indépendant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 w-full rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-gray-300"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setFilterOpen((v) => !v)}
              className={`flex items-center gap-2 h-9 px-3 rounded-md font-semibold text-sm border transition-colors ${
                filtreCount > 0
                  ? "border-[#6e1a36] text-[#6e1a36] bg-[#6e1a36]/5"
                  : "border-slate-300 bg-white text-black hover:bg-slate-50"
              }`}
            >
              <CiFilter className="w-3.5 h-3.5" />
              Filtrer
              {filtreCount > 0 && (
                <span className="bg-[#6e1a36] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                  {filtreCount}
                </span>
              )}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-gray-100 p-4 z-50">
                {[
                  { key: "organisme", label: "Caisse de cotisation", options: filterOptions(TNS_ORGANISMES, suivi, "tnsOrganisme") },
                  { key: "retraite", label: "Caisse de retraite", options: filterOptions(TNS_CAISSES_RETRAITE, suivi, "tnsCaisseRetraite") },
                  { key: "periodicite", label: "Périodicité", options: TNS_PERIODICITES },
                  // Texte libre : seules les valeurs réellement saisies.
                  { key: "regime", label: "Régime", options: filterOptions([], suivi, "tnsRegime").sort((a, b) => a.localeCompare(b, "fr")) },
                  { key: "paiement", label: "Mode de paiement", options: filterOptions(TNS_PAIEMENTS, suivi, "tnsPaiement") },
                  {
                    key: "statut",
                    label: "Statut",
                    options: [
                      { value: "late", label: "En retard" },
                      { value: "todo", label: "À traiter" },
                      { value: "ok", label: "À jour" },
                    ],
                  },
                  {
                    key: "missionResponsable",
                    label: "Missions · Responsable",
                    options: valeursMission(["responsableTNS", "responsableComptable"]),
                  },
                  { key: "missionStatut", label: "Missions · Statut", options: Object.keys(MISSION_STATUT_STYLE) },
                  { key: "missionOrganisme", label: "Missions · Organisme", options: valeursMission(["organisme"]) },
                ]
                  .filter((groupe) => groupe.options.length > 0)
                  .map((groupe) => (
                  <div key={groupe.key} className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">{groupe.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {groupe.options.map((opt) => {
                        const value = typeof opt === "string" ? opt : opt.value;
                        const label = typeof opt === "string" ? opt : opt.label;
                        const actif = flt[groupe.key].includes(value);
                        return (
                          <button
                            key={value}
                            onClick={() => toggleFiltre(groupe.key, value)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                              actif
                                ? "bg-[#6e1a36] border-[#6e1a36] text-white"
                                : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                            }`}
                          >
                            {actif && <Check className="w-2.5 h-2.5" />}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
                  <button
                    disabled={!filtreCount}
                    onClick={() => setFlt({ ...FILTRES_VIDES })}
                    className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Réinitialiser
                  </button>
                  <button
                    onClick={() => setFilterOpen(false)}
                    className="px-4 py-1.5 rounded-lg bg-[#6e1a36] hover:bg-[#5a1429] text-white text-xs font-semibold transition-colors"
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 h-9 px-1 rounded-lg border border-gray-200 bg-white">
            <button onClick={() => setYear((y) => y - 1)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Année précédente">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-sm font-semibold text-slate-700 px-1 w-12 text-center">{year}</span>
            <button onClick={() => setYear((y) => y + 1)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Année suivante">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white" style={{ fontSize: "0.72rem" }}>
          <colgroup>
            <col style={{ width: "220px" }} />
            <col style={{ width: "110px" }} />
            {MOIS_COURT.map((m) => (
              <col key={m} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Indépendant</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Périodicité</th>
              {MOIS_COURT.map((m, i) => (
                <th
                  key={m}
                  className={`px-2 py-2.5 text-center font-normal uppercase tracking-wide ${
                    i === curMonth ? "text-white bg-white/10" : "text-white/70"
                  }`}
                >
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {list.map((contact) => {
              const periodicite = contact.tnsPeriodicite || "Trimestrielle";
              const periods = tnsPeriods(periodicite, year);
              return (
                <tr
                  key={contact.id}
                  className={`bg-white ${contactBloque(contact) ? "ligne-bloquee" : ""}`}
                >
                  <td
                    className="px-3 py-2.5 font-medium text-slate-900 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setDetailContact(contact)}
                  >
                    <div className="font-semibold text-[#6e1a36]">
                      {contact.nom} {contact.prenoms}
                      {contactBloque(contact) && <BadgeBloque />}
                    </div>
                    <p
                      className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[190px]"
                      title={[tnsResume(contact), contact.tnsCommentaire].filter(Boolean).join("\n")}
                    >
                      {tnsResume(contact)}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">
                      {periodicite}
                    </span>
                  </td>
                  {periods.map((p) => {
                    const row = contact.tnsPeriodes?.find((r) => r.annee === year && r.periodeKey === p.key);
                    const state = periodState(row, p, today);
                    const meta = STATUT_META[state];
                    const inCurrentMonth = curMonth >= p.start && curMonth < p.start + p.span;
                    const caseMissions = missionsDeLAnnee(contact, year).filter((m) => m.periodeKey === p.key);
                    const missionsRetard = caseMissions.filter((m) => missionEnRetard(m, today)).length;
                    return (
                      <td key={p.key} colSpan={p.span} className="px-1 py-2 align-top">
                        <button
                          onClick={() => setEdit({ contact, period: p })}
                          title={`${p.long} · échéance ${fmtDate(p.due)}${
                            caseMissions.length
                              ? ` · ${caseMissions.length} mission${caseMissions.length > 1 ? "s" : ""}${
                                  missionsRetard ? ` dont ${missionsRetard} en retard` : ""
                                }`
                              : ""
                          } — cliquer pour saisir`}
                          className={`w-full h-full rounded-md border px-2 py-1.5 text-left transition-colors hover:shadow-sm ${meta.cellBg} ${meta.cellBorder} ${
                            inCurrentMonth ? "ring-1 ring-[#9B1B5D]/30" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className={`text-[11px] font-semibold ${meta.text}`}>{p.label}</span>
                            <span className="flex items-center gap-0.5">
                              {caseMissions.length > 0 && (
                                <span
                                  className={`text-[9px] font-bold min-w-4 h-4 px-1 rounded-full flex items-center justify-center ${
                                    missionsRetard ? "bg-rose-500 text-white" : "bg-slate-600 text-white"
                                  }`}
                                >
                                  {caseMissions.length}
                                </span>
                              )}
                              <span
                                className={`text-[9px] font-bold w-4 h-4 rounded flex items-center justify-center border ${
                                  row?.declarationEffectuee
                                    ? "bg-[#9B1B5D] border-[#9B1B5D] text-white"
                                    : "border-slate-300 text-slate-300"
                                }`}
                              >
                                D
                              </span>
                              <span
                                className={`text-[9px] font-bold w-4 h-4 rounded flex items-center justify-center border ${
                                  row?.paiementEffectue
                                    ? "bg-[#9B1B5D] border-[#9B1B5D] text-white"
                                    : "border-slate-300 text-slate-300"
                                }`}
                              >
                                P
                              </span>
                            </span>
                          </div>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr>
                <td colSpan={14} className="px-3 py-10 text-center text-slate-400 text-sm">
                  {(suivi || []).length === 0
                    ? "Aucun indépendant suivi. Activez le service TNS depuis une fiche de l'Annuaire."
                    : "Aucun indépendant ne correspond aux filtres"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center flex-wrap gap-4 mt-3 text-[11px] text-slate-500">
        <span className="flex items-center gap-1">
          <b className="inline-flex w-4 h-4 rounded items-center justify-center border border-slate-300 text-slate-500 font-bold text-[9px]">D</b>
          Déclaration
        </span>
        <span className="flex items-center gap-1">
          <b className="inline-flex w-4 h-4 rounded items-center justify-center border border-slate-300 text-slate-500 font-bold text-[9px]">P</b>
          Paiement
        </span>
        <span className="flex items-center gap-1">
          <b className="inline-flex min-w-4 h-4 px-1 rounded-full items-center justify-center bg-slate-600 text-white font-bold text-[9px]">2</b>
          Missions
          <b className="inline-flex min-w-4 h-4 px-1 rounded-full items-center justify-center bg-rose-500 text-white font-bold text-[9px] ml-1">1</b>
          dont en retard
        </span>
        {Object.values(STATUT_META).map((m) => (
          <span key={m.label} className="flex items-center gap-1.5">
            <i className={`inline-block w-2.5 h-2.5 rounded-full ${m.dot}`} />
            {m.label}
          </span>
        ))}
        <span className="ml-auto text-slate-400">Échéance : fin du mois suivant la période</span>
      </div>

      {edit && (
        <TNSPeriodModal
          contact={edit.contact}
          period={edit.period}
          onClose={() => setEdit(null)}
          onSaved={handleSaved}
          onOpenTodo={(contact) => setTodoContact(contact)}
        />
      )}

      {todoContact && (
        <CreateTodo setShowAddDiv={() => setTodoContact(null)} fetchTodos={fetchTodos} />
      )}

      {detailContact && (
        <ContactDetailOverlay contact={detailContact} onClose={() => setDetailContact(null)} />
      )}
    </div>
  );
}

export default SuiviTNS;
